import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Button, CircularProgress, Alert, Snackbar
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ManagedItemsTable from '../components/dc/ManagedItemsTable'
import { dcApi } from '../services/dc.api'
import { DCDemandMap } from '../types/dc.types'

// Persisted to localStorage (not just React Query's in-memory cache) so a page
// reload between recalculating demand and creating the order doesn't silently
// wipe the user's entered quantities.
const DEMAND_STORAGE_KEY = 'dc_demand_map'
const QTY_OVERRIDES_STORAGE_KEY = 'dc_qty_overrides'

function readStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : null
  } catch {
    return null
  }
}

function writeStorage(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // ignore storage errors (e.g. private browsing quota)
  }
}

export default function DCManagedItemsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(50)
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['dc-managed-items', page, limit, search],
    queryFn: () => dcApi.getManagedItems({ page, limit, search: search || undefined })
  })

  const [demandMap, setDemandMap] = useState<DCDemandMap | null>(() => readStorage<DCDemandMap>(DEMAND_STORAGE_KEY))
  const [qtyOverrides, setQtyOverrides] = useState<Record<number, number>>(
    () => readStorage<Record<number, number>>(QTY_OVERRIDES_STORAGE_KEY) ?? {}
  )

  const handleQtyOverride = (medicineId: number, qty: number) => {
    setQtyOverrides(prev => {
      const updated = { ...prev, [medicineId]: qty }
      writeStorage(QTY_OVERRIDES_STORAGE_KEY, updated)
      return updated
    })
  }

  const demandMutation = useMutation({
    mutationFn: () => dcApi.refreshDemand(),
    onSuccess: (result) => {
      setDemandMap(result)
      writeStorage(DEMAND_STORAGE_KEY, result)
      // Note: qty overrides are intentionally NOT cleared here — refreshing demand
      // must never silently discard quantities the user already entered.
      setSnackbar({ open: true, message: `Demand calculated for ${Object.keys(result).length} items`, severity: 'success' })
    },
    onError: (err: Error) => {
      setSnackbar({ open: true, message: err.message, severity: 'error' })
    }
  })

  const removeMutation = useMutation({
    mutationFn: (id: number) => dcApi.removeManagedItem(id),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Item removed from DC management', severity: 'success' })
      queryClient.invalidateQueries({ queryKey: ['dc-managed-items'] })
    },
    onError: (err: Error) => {
      setSnackbar({ open: true, message: err.message, severity: 'error' })
    }
  })

  const bulkRemoveMutation = useMutation({
    mutationFn: (ids: number[]) => dcApi.bulkRemoveManagedItems(ids),
    onSuccess: (result) => {
      setSnackbar({ open: true, message: `${result.removed} items removed from DC management`, severity: 'success' })
      setSelectedIds([])
      queryClient.invalidateQueries({ queryKey: ['dc-managed-items'] })
      queryClient.invalidateQueries({ queryKey: ['dc-eligible-items'] })
    },
    onError: (err: Error) => {
      setSnackbar({ open: true, message: err.message, severity: 'error' })
    }
  })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Failed to load managed items: {(error as Error).message}</Alert>
  }

  const items = data?.items || []
  const total = data?.pagination?.total || 0

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/orders')}>
            Back
          </Button>
          <Typography variant="h5">DC Managed Items</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({total} active)
          </Typography>
          {Object.keys(qtyOverrides).length > 0 && (
            <Typography variant="body2" color="warning.main" sx={{ ml: 1 }}>
              {Object.keys(qtyOverrides).length} qty override(s) active
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {Object.keys(qtyOverrides).length > 0 && (
            <Button
              variant="text"
              color="warning"
              onClick={() => {
                setQtyOverrides({})
                writeStorage(QTY_OVERRIDES_STORAGE_KEY, {})
              }}
            >
              Clear Overrides
            </Button>
          )}
          {selectedIds.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => bulkRemoveMutation.mutate(selectedIds)}
              disabled={bulkRemoveMutation.isPending}
            >
              {bulkRemoveMutation.isPending ? 'Removing...' : `Remove ${selectedIds.length}`}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => demandMutation.mutate()}
            disabled={demandMutation.isPending}
          >
            {demandMutation.isPending ? 'Calculating...' : 'Refresh Demand'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate('/orders/eligible')}
          >
            Add Items
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/orders/create', { state: { qtyOverrides } })}
            disabled={total === 0}
          >
            Create Order
          </Button>
        </Box>
      </Box>

      <ManagedItemsTable
        items={items}
        total={total}
        page={page}
        limit={limit}
        search={search}
        demandMap={demandMap}
        qtyOverrides={qtyOverrides}
        onQtyOverride={handleQtyOverride}
        onSearchChange={(s) => { setSearch(s); setPage(1) }}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1) }}
        onRemove={(id) => removeMutation.mutate(id)}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  )
}
